import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Spinner from 'react16-spinjs';
import classNames from 'classnames';
import {
    MIC_FAILURE_TYPES,
    VERIFICATION_RESULT,
    VERIFICATION_STATUS,
} from '../TROInstructionsConstants';
import MicPermissionsTestSuccessCard from './MicPermissionsTestSuccessCard';
import { TRC_ERROR_AUDIO, TRO_MIC_TEST_TEXT } from '../../../troConstants';
import { redirectToAR } from '../../../troUtils';
import { SPINNER } from '../../../../constants/components';
import MicCardContainer from './MicCardContainer';

const NO_ATTEMPTS = 0;
const FIRST_ATTEMPT = 1;
const SECOND_ATTEMPT = 2;
const THIRD_ATTEMPT = 3;
class MicTestCard extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isMicSuccessCardActive: false,
            shouldReinitializeRecorder: false,
        };
    }

    componentDidMount() {
        this.props.narrateScreen(this.props.isExternalURLSourcePresent, this.props.arLanguage);
        if(!this.props.isExternalURLSourcePresent) this.props.trackStartPreAssessmentStep();
    }

    // eslint-disable-next-line consistent-return
    componentDidUpdate(prevProps) {
        const {
            verificationResult,
            verificationStatus,
            attemptCount,
            onFirstTestFailure,
            onFailure,
            recordingStream,
            playAvatarAudio,
            onTestSuccess,
            isExternalURLSourcePresent,
            arLanguage,
            isBenchmarkModeEnabled,
            trackFailedMicCheck,
        } = this.props;
        const hasResultChanged = verificationResult !== prevProps.verificationResult;
        const hasStatusChanged = verificationStatus !== prevProps.verificationStatus;
        const hasAttempts = attemptCount !== NO_ATTEMPTS;
        const hasIncreasedAttempts = attemptCount > prevProps.attemptCount;

        if (hasIncreasedAttempts) this.setDoNotReinitializeRecorder();

        if (recordingStream && !recordingStream.active) {
            onFailure(MIC_FAILURE_TYPES.ACCESS_FAILED);
        }

        if (hasStatusChanged && verificationResult === VERIFICATION_RESULT.PASSED) onTestSuccess(isExternalURLSourcePresent, arLanguage);

        if (hasStatusChanged && verificationStatus === VERIFICATION_STATUS.ERROR) {
            onFailure(MIC_FAILURE_TYPES.MIC_TEST_ERROR);
            playAvatarAudio(TRC_ERROR_AUDIO.thatDidntWorkTwo);
        }

        if (hasAttempts && hasResultChanged && verificationResult === VERIFICATION_RESULT.FAILED) {
            if (!isExternalURLSourcePresent) trackFailedMicCheck(attemptCount);
            switch (attemptCount) {
            case FIRST_ATTEMPT:
                onFirstTestFailure(isExternalURLSourcePresent, arLanguage);
                this.setReinitializeRecorder();
                break;
            case SECOND_ATTEMPT:
                if (isExternalURLSourcePresent) {
                    const queryParams = {
                        hasTroError: true,
                        benchmarkMode: isBenchmarkModeEnabled,
                    };
                    this.props.trackTroMicCheck();
                    this.props.onSecondMicFailure().then(() => redirectToAR(queryParams));
                } else {
                    onFailure(MIC_FAILURE_TYPES.SECOND_MIC_TEST_FAILED);
                    playAvatarAudio(TRC_ERROR_AUDIO.trcMicVerificationFailure);
                    this.setReinitializeRecorder();
                }
                break;
            case THIRD_ATTEMPT:
                onFailure(MIC_FAILURE_TYPES.THIRD_MIC_TEST_FAILED);
                playAvatarAudio(TRC_ERROR_AUDIO.thatDidntWorkOne);
                break;
            default:
                break;
            }
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    setReinitializeRecorder = () => this.setState({ shouldReinitializeRecorder: true });

    setDoNotReinitializeRecorder = () => this.setState({ shouldReinitializeRecorder: false });

    renderSuccessCard = () => (
        <MicPermissionsTestSuccessCard text="Microphone is working." onDone={this.props.onDone} />
    );

    isVisualizerActive = () =>
        this.props.isRecordingInProgress && this.props.recordingStream;

    onArrowButtonClick = () => {
        const { onSuccess } = this.props;
        onSuccess();
        this.setState({ isMicSuccessCardActive: true });
        if(this.props.isExternalURLSourcePresent) {
            this.props.trackTroMicCheck();
        }
    };

    renderPromptCard = () => {
        const { isRecordingInProgress, isSpinnerVisible } = this.props;
        const { shouldReinitializeRecorder } = this.state;

        return (
            <MicCardContainer
                isSpinnerVisible={isSpinnerVisible}
                shouldReinitializeRecorder={shouldReinitializeRecorder}
                onArrowButtonClick={this.onArrowButtonClick}
                {...this.props}
            >
                <div className="instruction-content">
                    <div
                        className={classNames('example-reading-text', {
                            highlight: isRecordingInProgress,
                        })}
                    >
                        {TRO_MIC_TEST_TEXT}
                    </div>
                    {isSpinnerVisible && <Spinner config={SPINNER.DARK} />}
                    <div className="mic-test-image" />
                </div>
            </MicCardContainer>
        );
    };

    render() {
        const { isMicSuccessCardActive } = this.state;
        if (isMicSuccessCardActive) {
            return this.renderSuccessCard();
        }
        return this.renderPromptCard();
    }
}

MicTestCard.propTypes = {
    recordingStream: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    onDone: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    cleanup: PropTypes.func.isRequired,
    narrateScreen: PropTypes.func.isRequired,
    isRecordingInProgress: PropTypes.bool.isRequired,
    updateVerificationProgress: PropTypes.func.isRequired,
    onStopRecording: PropTypes.func,
    verificationResult: PropTypes.string.isRequired,
    verificationStatus: PropTypes.string.isRequired,
    isScreenBlocked: PropTypes.bool.isRequired,
    attemptCount: PropTypes.number.isRequired,
    onFirstTestFailure: PropTypes.func.isRequired,
    playAvatarAudio: PropTypes.func.isRequired,
    isSpinnerVisible: PropTypes.bool,
    onTestSuccess: PropTypes.func,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    arLanguage: PropTypes.string.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    onSecondMicFailure: PropTypes.func,
    trackTroMicCheck: PropTypes.func,
    trackFailedMicCheck: PropTypes.func,
    trackStartPreAssessmentStep: PropTypes.func.isRequired,
};

MicTestCard.defaultProps = {
    recordingStream: false,
    isSpinnerVisible: false,
    trackTroMicCheck: () => { },
    onTestSuccess: () => {},
    onSecondMicFailure: () => {},
    trackFailedMicCheck: () => {},
    onStopRecording: () => {},
};

export default MicTestCard;
